import React, { forwardRef, useContext } from 'react';
import _merge from 'lodash/merge';
import { getContext } from 'common';

import { SearchwChipsContext } from 'components/SearchwChips';

import { ToggleGroupButton } from 'components/ToggleButtonGroup';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';

const SMALL_TEXT_PROPS = {
  style: {
    ...Tokens.TYPOGRAPHY.LABEL,
    lineHeight: Tokens.LINE_HEIGHT.LINE_HEIGHT_NONE,
  }
};

export interface SearchwChipsGlobalLogicButtonProps extends React.ComponentPropsWithRef<'button'> {
  /** Children prop is where button content resides. The content of the button including `ButtonText` and `ButtonIcon`. */
  children: React.ReactNode;
  /** The size of the component. Default is "medium". */
  size?: 'small' | 'medium';
  /** To override the SearchwChipsGlobalLogicButton theme please refer to "Readme" tab to learn more. */
  theme?: any;
}

/**
 * <SearchwChipsGlobalLogicButton/> extend `ToggleGroupButton` with customized style to make global 'AND' or 'OR' for `SearchwChips`.
 * All `ToggleGroupButton` props are available through this component. Refer to `ToggleGroupButton`'s documentation for more information.
 * Note: `onChange`'s `ToggleGroupButton` prop is not required by `SearchwChipsGlobalLogicButton`.
 */
const SearchwChipsGlobalLogicButton = forwardRef<HTMLElement, SearchwChipsGlobalLogicButtonProps>((props: SearchwChipsGlobalLogicButtonProps, ref) => getContext(({
  theme: {
    SEARCH_W_CHIPS: {
      globalLogic: globalTheme
    }
  }
}) => {
  const {
    size: sizeProp,
    theme: themeProp,
    ...otherProps
  } = props;
  const size = sizeProp || (SearchwChipsContext && useContext(SearchwChipsContext).size) || 'medium';
  const resolvedTheme = _merge({}, globalTheme, themeProp);
  const buttonTextProps = size === 'small' ? SMALL_TEXT_PROPS : {};
  return (
    <ToggleGroupButton
      size={size}
      buttonTextProps={buttonTextProps}
      theme={resolvedTheme}
      ref={ref}
      data-variant="search-w-chips-toggle"
      {...otherProps}
      onChange={() => {}}
    />);
}));

SearchwChipsGlobalLogicButton.defaultProps = {
  theme: {},
  size: undefined,
};

SearchwChipsGlobalLogicButton.displayName = 'SearchwChipsGlobalLogicButton';
export default SearchwChipsGlobalLogicButton;
